import React, { useState, useContext, useEffect, useMemo } from 'react';
import { SquarePen, Trash2, LogOut, Music4, Headphones, Languages, CheckCircle, XCircle, CopyCheck, TriangleAlert, Users, ListOrdered } from 'lucide-react';
import { searchCantiques, findDuplicateGroups } from '../utils/search';
import { toast } from 'react-toastify';
import { CantiqueContextAPI } from '../Context/CantiqueContext';
import CantiqueForm from './CantiqueForm';
import AdminAddLanguage from './AdminAddLanguage';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import DuplicateModal from '../components/DuplicateModal';
import Highlight from '../components/Highlight';
import ThemeToggle from '../components/ThemeToggle';
import AdminVisitors from './AdminVisitors';
import RenumberModal from './RenumberModal';

const AdminDashboard = () => {
  const { allCantiques, searchTerm, setSearchTerm, modifyCantique, deleteCantique, addCantique, logout, availableLanguages, reloadCantiques } = useContext(CantiqueContextAPI);
  const [isRenumberOpen, setIsRenumberOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [editingCantique, setEditingCantique] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, cantique: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [audioFilter, setAudioFilter] = useState('all');
  const [section, setSection] = useState('cantiques');
  const rowsPerPage = 8;

  const [languageFilter, setLanguageFilter] = useState('all');
  const [showDuplicates, setShowDuplicates] = useState(false);

  const duplicateGroups = useMemo(() => findDuplicateGroups(allCantiques), [allCantiques]);

  // id -> list of duplicate notes shown next to the title
  const duplicateNotes = useMemo(() => {
    const notes = new Map();
    duplicateGroups.forEach(group => {
      group.items.forEach(item => {
        const others = group.items.filter(other => other.id !== item.id);
        const text = group.type === 'number'
          ? `Numéro ${item.number} partagé avec « ${others.map(o => o.title.trim()).join(' », « ')} »`
          : `Titre en double : n° ${others.map(o => o.number ?? '?').join(', ')}`;
        notes.set(item.id, [...(notes.get(item.id) || []), text]);
      });
    });
    return notes;
  }, [duplicateGroups]);

  const duplicateCantiques = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    [...duplicateGroups]
      .sort((a, b) => Math.min(...a.items.map(i => i.number ?? Infinity)) - Math.min(...b.items.map(i => i.number ?? Infinity)))
      .forEach(group => group.items.forEach(item => {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          ordered.push(item);
        }
      }));
    return ordered;
  }, [duplicateGroups]);

  const hasAudio = (cantique) => {
    return Boolean(
      cantique.audio ||
      cantique.audioUrl ||
      cantique.hasAudio ||
      cantique.media?.audio ||
      cantique.files?.some(file => file.type?.startsWith('audio'))
    );
  };

  const baseCantiques = (showDuplicates ? duplicateCantiques : allCantiques).filter(cantique => {
    if (audioFilter === 'with' && !hasAudio(cantique)) {
      return false;
    }

    if (audioFilter === 'without' && hasAudio(cantique)) {
      return false;
    }

    if (languageFilter !== 'all' && cantique.langue !== languageFilter) {
      return false;
    }

    return true;
  });
  const filteredCantiques = searchCantiques(baseCantiques, searchTerm, { includeLanguage: true });

  const handleLanguageChange = async (cantique, nextLangue) => {
    if (!nextLangue || nextLangue === cantique.langue) return;
    const confirmed = window.confirm(
      `Changer la langue du cantique n° ${cantique.number ?? '?'} « ${cantique.title.trim()} » ?\n\n${cantique.langue || 'Non spécifiée'} → ${nextLangue}`
    );
    if (!confirmed) return;
    const updated = await modifyCantique(cantique.id, { langue: nextLangue });
    if (updated) toast.success(`Langue mise à jour : ${nextLangue}`);
  };

  const audioAssociatedCount = allCantiques.filter(hasAudio).length;
  const audioMissingCount = allCantiques.length - audioAssociatedCount;
  const pageCount = Math.max(1, Math.ceil(filteredCantiques.length / rowsPerPage));
  const normalizedPage = Math.min(currentPage, pageCount);
  const pageStart = filteredCantiques.length === 0 ? 0 : (normalizedPage - 1) * rowsPerPage + 1;
  const pageEnd = Math.min(filteredCantiques.length, normalizedPage * rowsPerPage);
  const pagedCantiques = filteredCantiques.slice((normalizedPage - 1) * rowsPerPage, normalizedPage * rowsPerPage);

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, audioFilter, languageFilter, showDuplicates]);

  const getPageItems = () => {
    const items = [];
    if (pageCount <= 7) {
      for (let i = 1; i <= pageCount; i += 1) {
        items.push(i);
      }
      return items;
    }

    items.push(1);
    const leftBound = Math.max(2, normalizedPage - 1);
    const rightBound = Math.min(pageCount - 1, normalizedPage + 1);

    if (leftBound > 2) items.push('left-dots');
    for (let i = leftBound; i <= rightBound; i += 1) {
      items.push(i);
    }
    if (rightBound < pageCount - 1) items.push('right-dots');
    items.push(pageCount);
    return items;
  };

  const pageItems = getPageItems();

  const handleEditCantique = (cantique) => {
    setEditingCantique(cantique);
    setIsFormOpen(true);
  };

  const buildCantiquePayload = (formData) => ({
    title: formData.title,
    langue: formData.langue,
    couplets: formData.couplets,
    refrain: formData.refrain,
    hasAudio: formData.hasAudio ?? false,
    audio: formData.audio ?? false,
    audioUrl: formData.audioUrl ?? null,
    audioFileName: formData.audioFileName ?? null,
    audioMimeType: formData.audioMimeType ?? null,
  });

  const handleSaveCantique = async (formData) => {
    const payload = buildCantiquePayload(formData);

    if (editingCantique) {
      const updated = await modifyCantique(editingCantique.id, payload);
      if (!updated) return false;
      toast.success("Cantique modifié avec succès");
    } else {
      const added = await addCantique(payload);
      if (added) {
        toast.success("Cantique bien créé");
      } else {
        return false;
      }
    }
    setEditingCantique(null);
    return true;
  };

  const handleDeleteCantique = (cantique) => {
    setDeleteConfirmation({ isOpen: true, cantique });
  };

  const confirmDelete = () => {
    if (deleteConfirmation.cantique) {
      deleteCantique(deleteConfirmation.cantique.id);
      toast.success("Cantique supprimé avec succès");
      setDeleteConfirmation({ isOpen: false, cantique: null });
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingCantique(null);
  };

  return (
    <div className="admin">
      <div className="admin__container">
        {isFormOpen && (
          <CantiqueForm 
            isOpen={isFormOpen} 
            onClose={handleFormClose}
            cantique={editingCantique}
            onSave={handleSaveCantique}
          />
        )}
        <RenumberModal isOpen={isRenumberOpen} onClose={() => setIsRenumberOpen(false)} onDone={reloadCantiques} />
        {isLanguageModalOpen && <AdminAddLanguage isOpen={isLanguageModalOpen} onClose={() => setIsLanguageModalOpen(false)} />}
        {deleteConfirmation.isOpen && (
          <ConfirmDeleteModal
            isOpen={deleteConfirmation.isOpen}
            onClose={() => setDeleteConfirmation({ isOpen: false, cantique: null })}
            cantiqueName={deleteConfirmation.cantique?.title}
            onConfirm={confirmDelete}
          />
        )}
        
        <header className="admin__header">
          <div className="admin__header__left">
            <h2 className="admin__title">Administration</h2>
            <div className="admin__title__sub">Gérer les cantiques et langues</div>
          </div>
          <div className="admin__header__actions">
            <ThemeToggle />
            <button className="admin__logout" onClick={logout} title="Se déconnecter">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <div className="admin__tabs" role="tablist" aria-label="Sections de l’administration">
          <button
            type="button"
            role="tab"
            aria-selected={section === 'cantiques'}
            className={`admin__tabs__tab ${section === 'cantiques' ? 'active' : ''}`}
            onClick={() => setSection('cantiques')}
          >
            <Music4 size={15} />
            <span>Cantiques</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={section === 'visiteurs'}
            className={`admin__tabs__tab ${section === 'visiteurs' ? 'active' : ''}`}
            onClick={() => setSection('visiteurs')}
          >
            <Users size={15} />
            <span>Visiteurs</span>
          </button>
        </div>

        {section === 'visiteurs' ? <AdminVisitors /> : (
        <>
        <div className="admin__results">
        <div className="admin__results__list">
            <div className="admin__results__list__card">
              <div className="admin__results__list__card__images">
                 <span className="admin__results__list__card__images__icones">
                    <Music4 size={20} />
                 </span>
              </div>
              <div className="admin__results__list__card__texts">
                <h2 className="admin__results__list__card__title">{allCantiques.length}</h2>
                <div className="admin__results__list__card__sub">Cantiques</div>
              </div>
            </div>
            <div className="admin__results__list__card">
              <div className="admin__results__list__card__images">
                 <span className="admin__results__list__card__images__icones">
                    <Headphones size={20} />
                 </span>
              </div>
              <div className="admin__results__list__card__texts">
                <h2 className="admin__results__list__card__title">{audioAssociatedCount}</h2>
                <div className="admin__results__list__card__sub">Audio associés</div>
              </div>
            </div>
            <div className="admin__results__list__card">
              <div className="admin__results__list__card__images">
                 <span className="admin__results__list__card__images__icones">
                    <Languages size={20} />
                 </span>
              </div>
              <div className="admin__results__list__card__texts">
                <h2 className="admin__results__list__card__title">{availableLanguages.length}</h2>
                <div className="admin__results__list__card__sub">Langues</div>
              </div>
            </div>
            <div className="admin__results__list__card">
              <div className="admin__results__list__card__images">
                 <span className="admin__results__list__card__images__icones">
                    <Music4 size={20} />
                 </span>
              </div>
              <div className="admin__results__list__card__texts">
                <h2 className="admin__results__list__card__title">{audioMissingCount}</h2>
                <div className="admin__results__list__card__sub">Cantiques sans audios</div>
              </div>
            </div>
        </div>
        </div>

        <div className="admin__preview">
          <div className="admin__preview__header">
            <div className="admin__preview__header__search">
              <input
                type="text"
                placeholder="Rechercher un cantique..."
                className="admin__preview__header__search__input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="admin__preview__header__filters">
              <div className="admin__preview__header__filter">
                <span>Audio</span>
                <button
                  type="button"
                  className={audioFilter === 'all' ? 'active' : ''}
                  onClick={() => setAudioFilter('all')}
                >
                  Tous
                </button>
                <button
                  type="button"
                  className={audioFilter === 'with' ? 'active' : ''}
                  onClick={() => setAudioFilter('with')}
                >
                  Associés
                </button>
                <button
                  type="button"
                  className={audioFilter === 'without' ? 'active' : ''}
                  onClick={() => setAudioFilter('without')}
                >
                  Non associés
                </button>
              </div>
              <label className="admin__preview__header__filter">
                <span>Langue</span>
                <select
                  className="admin__preview__header__filter__select"
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                >
                  <option value="all">Toutes</option>
                  {availableLanguages.map(l => (
                    <option key={l.id} value={l.name}>{l.name}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="admin__preview__header__buttons">
              <button 
                className="admin__preview__header__buttons__btn primary"
                onClick={() => {
                  setEditingCantique(null);
                  setIsFormOpen(true);
                }}
              >
                <span>+</span>
                <span>Ajouter un cantique</span>
              </button>
              <button 
                className="admin__preview__header__buttons__btn"
                onClick={() => setIsLanguageModalOpen(true)}
              >
                <span>+</span>
                <span>Ajouter une langue</span>
              </button>
              <button
                type="button"
                className="admin__preview__header__buttons__btn"
                onClick={() => setIsRenumberOpen(true)}
                title="Supprimer les trous de numérotation (aperçu avant confirmation)"
              >
                <ListOrdered size={15} />
                <span>Renuméroter</span>
              </button>
            </div>
          </div>

          {(duplicateGroups.length > 0 || showDuplicates) && (
            <div className={`admin__duplicates ${showDuplicates ? 'is-active' : ''}`} role="status">
              <div className="admin__duplicates__text">
                {duplicateGroups.length > 0 ? <TriangleAlert size={16} /> : <CopyCheck size={16} />}
                <span>
                  {duplicateGroups.length > 0
                    ? `${duplicateGroups.length} doublon${duplicateGroups.length > 1 ? 's' : ''} détecté${duplicateGroups.length > 1 ? 's' : ''} (numéros ou titres identiques). À vérifier avant toute correction.`
                    : 'Aucun doublon restant.'}
                </span>
              </div>
              <button type="button" onClick={() => setShowDuplicates(v => !v)}>
                {showDuplicates ? 'Voir tous les cantiques' : 'Afficher les doublons'}
              </button>
            </div>
          )}

          <div className="admin__preview__content">
            {filteredCantiques.length > 0 ? (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Titre</th>
                      <th>Langue</th>
                      <th>Audio</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedCantiques.map((cantique, index) => (
                    <tr key={cantique.id}>
                        <td>{cantique.number ?? (pageStart + index)}</td>
                        <td>
                          <Highlight text={cantique.title} query={searchTerm} />
                          {duplicateNotes.get(cantique.id)?.map(note => (
                            <span key={note} className="admin__duplicate-badge" title={note}>
                              <TriangleAlert size={12} />
                              {note}
                            </span>
                          ))}
                        </td>
                        <td>
                          <select
                            className="admin__language-select"
                            value={cantique.langue || ''}
                            onChange={(e) => handleLanguageChange(cantique, e.target.value)}
                            aria-label={`Langue du cantique ${cantique.title}`}
                            title="Corriger la langue (confirmation demandée)"
                          >
                            {!availableLanguages.some(l => l.name === cantique.langue) && (
                              <option value={cantique.langue || ''}>{cantique.langue || 'N/A'}</option>
                            )}
                            {availableLanguages.map(l => (
                              <option key={l.id} value={l.name}>{l.name}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <div className="audio-status">
                            {hasAudio(cantique) ? (
                              <span className="audio-status__with">
                                <CheckCircle size={16} />
                                Audio associé
                              </span>
                            ) : (
                              <span className="audio-status__without">
                                <XCircle size={16} />
                                Aucun audio
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="actions">
                          <button 
                            title="Modifier"
                            onClick={() => handleEditCantique(cantique)}
                          >
                            <SquarePen size={16} />
                          </button>
                          <button 
                            title="Supprimer"
                            onClick={() => handleDeleteCantique(cantique)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="admin__preview__pagination">
                  <div className="pagination__info">
                    Affichage {pageStart} à {pageEnd} sur {filteredCantiques.length} cantiques
                  </div>
                  <div className="pagination__controls">
                    <button
                      className="pagination__btn"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={normalizedPage === 1}
                    >
                      ‹
                    </button>
                    <div className="pagination__numbers">
                      {pageItems.map(item =>
                        item === 'left-dots' || item === 'right-dots' ? (
                          <span key={item} className="pagination__dots">…</span>
                        ) : (
                          <button
                            key={item}
                            className={`pagination__number ${item === normalizedPage ? 'active' : ''}`}
                            onClick={() => setCurrentPage(item)}
                          >
                            {item}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      className="pagination__btn"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, pageCount))}
                      disabled={normalizedPage === pageCount}
                    >
                      ›
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state">
                Aucun cantique ne correspond à votre recherche
              </div>
            )}
          </div>

          <div className="admin__preview__details">
            {/* Détails d'un cantique sélectionné – à implémenter plus tard */}
          </div>
        </div>
        </>
        )}
      </div>
      <DuplicateModal />
    </div>
  );
};

export default AdminDashboard;